import React, {
  createContext,
  PropsWithChildren,
  useEffect,
  useState,
} from "react";
import { Account } from "@services/account/types";

import { useSimpleReducer } from "@common/hooks";
import { useErrorNotification } from "@common/components/Notification";
import { SchedulingOption } from "@services/rooms/types";
import { getLocations } from "@services/locations";
import { Location } from "@services/locations/types";
import { getAccount, getLinkableAccounts } from "@services/account";
import { sortByAccountName } from "@common/components/Header/utils/utils";
import { useAssetNavigationParams } from "@pages/Assets/common/const";
import { useLocation } from "react-router-dom";
import { useUserSelectedAccounts } from "@services/account/hooks/useUserSelectedAccounts";
import { useAuthenticatedUser } from "@services/user/hooks/useAuthenticatedUser";
import { canViewLocation, canViewUser } from "@common/utils/user";
import { hasAccountReachedRoomLimit } from "@pages/Assets/Rooms/utils/hasAccountReachedRoomLimit";

export interface IFieldValueContext {
  updateAccountId(id: string): void;
  accounts?: Account[];
  locations?: Location[];
  schedulingOptions: { id: SchedulingOption; name: string }[];
  roomTypes?: { id: string; name: string }[];
  banDetails?: { id: number; name: string }[];
  accountId?: string;
  hasAccountReachedRoomLimit: boolean;
}

const schedulingOptions = [
  { id: SchedulingOption.None, name: "None" },
  { id: SchedulingOption.VCScheduling, name: "VC Scheduling" },
  { id: SchedulingOption.RoomOnlyScheduling, name: "Room Only Scheduling" },
];

export const roomTypes = [
  { id: "Audio", name: "Audio" },
  { id: "AVI-SPL Hosting PoP", name: "AVI-SPL Hosting PoP" },
  { id: "Board Room", name: "Board Room" },
  { id: "Collaborative Classroom", name: "Collaborative Classroom" },
  { id: "Cubicle", name: "Cubicle" },
  { id: "Customer Data Center", name: "Customer Data Center" },
  { id: "Data Closet", name: "Data Closet" },
  { id: "Event Space", name: "Event Space" },
  { id: "Huddle Mobile", name: "Huddle Mobile" },
  { id: "Huddle Space", name: "Huddle Space" },
  { id: "Lecture Hall", name: "Lecture Hall" },
  { id: "Meeting Room", name: "Meeting Room" },
  { id: "Office", name: "Office" },
  { id: "Teaching Space", name: "Teaching Space" },
  { id: "Telepresence Room", name: "Telepresence Room" },
  { id: "Virtual Room", name: "Virtual Room" },
];

export const FieldValueContext = createContext<IFieldValueContext>({
  schedulingOptions,
  roomTypes,
  updateAccountId: () => {},
  hasAccountReachedRoomLimit: false,
});

export const FieldValueProvider = (
  props: PropsWithChildren<{ account?: Account }>
) => {
  const { navigationParams } = useAssetNavigationParams();
  const [user] = useAuthenticatedUser();
  const canViewLocations = canViewLocation(user);
  const canViewUsers = canViewUser(user);
  const location = useLocation();
  const [userSelectedAccounts] = useUserSelectedAccounts();
  const accountState = (location.state as { accountId?: string }) ?? {};
  const accountid = navigationParams.fromAccount;
  const dispatchError = useErrorNotification();
  const [account, setAccount] = useState<Account>();

  const dispatchInformation = (account?: Account) => {
    if (
      account?.details?.customizedRoomTypes &&
      account?.details?.customizedRoomTypes.length > 0
    ) {
      dispatch({
        roomTypes: account?.details?.customizedRoomTypes.map((type) => {
          return { id: type, name: type };
        }),
      });
    } else {
      dispatch({ roomTypes });
    }
    if (
      account?.details?.banDetails &&
      account?.details?.banDetails.length > 0
    ) {
      dispatch({
        banDetails: account?.details?.banDetails.map((banDetail) => {
          return {
            id: banDetail.id ?? -1,
            name:
              (banDetail.ban ?? "") +
              (banDetail.description ? ` (${banDetail.description})` : ""),
          };
        }),
      });
    }
  };

  const [state, dispatch] = useSimpleReducer<
    Omit<IFieldValueContext, "hasAccountReachedRoomLimit">
  >({
    schedulingOptions,
    roomTypes,
    updateAccountId: (id: string) => {
      fetchLocations(id);
      fetchAccount(id);
    },
  });

  const fetchAccount = async (id: string) => {
    try {
      const account: Account = await getAccount(id);
      dispatch({ accountId: id });
      setAccount(account);
      dispatchInformation(account);
    } catch (e) {
      dispatchError(e, { displayMessage: true });
    }
  };

  const fetchAccounts = async () => {
    try {
      let tmp = (await getLinkableAccounts()).sort(sortByAccountName);
      dispatch({
        accounts: tmp,
      });
      if (!props.account) {
        if (accountid) {
          fetchLocations(accountid);
        }
      }
    } catch (e) {
      dispatchError(e, { displayMessage: true });
    }
  };

  const fetchLocations = async (id: string) => {
    dispatch({ accountId: id });

    try {
      dispatch({
        locations: (
          await getLocations(
            { accountId: [id] },
            { queryType: "provisioning", orderBy: "name asc" }
          )
        ).list as Location[],
      });
    } catch (e) {
      dispatchError(e, { displayMessage: true });
    }
  };

  useEffect(() => {
    const accountID =
      accountState.accountId ?? userSelectedAccounts.length === 1
        ? userSelectedAccounts[0]
        : props.account?.id;
    if (accountID && canViewLocations) {
      fetchLocations(accountID);
      if (canViewUsers) fetchAccounts();
    }
    dispatchInformation(props.account);

    if (!props.account && accountid) {
      fetchAccount(accountid);
    }
  }, [props.account]);

  return (
    <FieldValueContext.Provider
      value={{
        ...state,
        hasAccountReachedRoomLimit:
          hasAccountReachedRoomLimit(account) ?? false,
      }}
    >
      {props.children}
    </FieldValueContext.Provider>
  );
};
